import { invokeEdgeFunction } from '@/utils/invokeEdgeFunction';

// IST timezone constants
export const IST_TIMEZONE = "Asia/Kolkata";
export const IST_OFFSET_MINUTES = 330; // UTC+5:30 in minutes
export const IST_OFFSET_HOURS = 5.5;

// Server time response interface
interface ServerTimeResponse {
  success: boolean;
  data?: {
    utc: string;
    ist: string;
    timestamp: number;
    timezone: string;
    formatted: {
      date: string;
      time: string;
      datetime: string;
    };
  };
  error?: string;
}

/**
 * Calculate the server time offset by fetching server time and comparing with client time
 * Returns the millisecond difference between server UTC time and client time
 * @throws Error if server time cannot be fetched
 */
export async function calculateServerTimeOffset(): Promise<number> {
  const response = await invokeEdgeFunction<ServerTimeResponse>(
    "get-real-server-time"
  );

  if (!response.success || !response.data) {
    console.error("Invalid server time response:", response);
    throw new Error("Invalid server time response from server");
  }

  // Parse the server UTC time
  const serverUTCTime = new Date(response.data.utc);
  const localTime = new Date();

  // Calculate offset between server UTC and local time
  const offset = serverUTCTime.getTime() - localTime.getTime();

  return offset;
}

/**
 * Apply server time offset to get server-synchronized time
 * @param timestamp - The client timestamp (usually Date.now())
 * @param offset - The server time offset in milliseconds
 * @returns Date object adjusted to server time
 */
export function applyOffset(timestamp: number, offset: number): Date {
  return new Date(timestamp + offset);
}

/**
 * Get server-synchronized time in IST using proper timezone APIs
 * @param offset - The server time offset in milliseconds
 * @returns Date object representing current server time in IST
 */
export function getServerTimeIST(offset: number): Date {
  const serverUTCTime = applyOffset(Date.now(), offset);
  return serverUTCTime;
}

/**
 * Convert any date to IST using proper timezone APIs
 */
export function toIST(date: Date): Date {
  return date;
}

/**
 * Format date as IST string using proper timezone APIs
 */
export function formatISTDate(date: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: IST_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/**
 * Format time as IST string (HH:MM:SS) using proper timezone APIs
 */
export function formatISTTime(date: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: IST_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
}

export function formatDisplayDate(date: string | null | undefined): string {
  if (!date) {
    return "N/A";
  }

  const [year, month, day] = date.split("-");

  if (!year || !month || !day) {
    return date;
  }

  return `${day}/${month}/${year}`;
}

export function formatDisplayTime(time: string | null | undefined): string {
  if (!time) {
    return "N/A";
  }

  const [hoursPart, minutesPart] = time.split(":");
  const hours = Number(hoursPart);
  const minutes = Number(minutesPart);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return time;
  }

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;

  return `${displayHours.toString().padStart(2, "0")}:${minutesPart.padStart(2, "0")} ${period}`;
}

/**
 * Format datetime as IST string (YYYY-MM-DD HH:MM:SS) using proper timezone APIs
 */
export function formatISTDateTime(date: Date): string {
  const dateStr = formatISTDate(date);
  const timeStr = formatISTTime(date);
  return `${dateStr} ${timeStr}`;
}

/**
 * Check if current server time is within the specified date and time range
 * @param targetDate - Target date in YYYY-MM-DD format
 * @param startTime - Start time in HH:MM:SS format
 * @param endTime - End time in HH:MM:SS format
 * @param offset - Server time offset in milliseconds
 */
export function isTimeInRange(
  targetDate: string,
  startTime: string,
  endTime: string,
  offset: number
): boolean {
  const serverTime = getServerTimeIST(offset);
  const serverDateStr = formatISTDate(serverTime);
  const serverTimeStr = formatISTTime(serverTime);

  // Check if dates match
  if (serverDateStr !== targetDate) {
    return false;
  }

  // Check if current time is within the range
  return serverTimeStr >= startTime && serverTimeStr <= endTime;
}

/**
 * Check if current server time is within the specified date range
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @param offset - Server time offset in milliseconds
 */
export function isDateInRange(
  startDate: string,
  endDate: string,
  offset: number
): boolean {
  const serverTime = getServerTimeIST(offset);
  const serverDateStr = formatISTDate(serverTime);

  return serverDateStr >= startDate && serverDateStr <= endDate;
}

/**
 * Get server time as ISO string for database storage
 * @param offset - Server time offset in milliseconds
 */
export function getServerTimeISOString(offset: number): string {
  const serverTime = applyOffset(Date.now(), offset);
  return serverTime.toISOString();
}

/**
 * Compare two IST date strings
 */
export function compareISTDates(date1: string, date2: string): number {
  const d1 = new Date(date1 + "T00:00:00+05:30");
  const d2 = new Date(date2 + "T00:00:00+05:30");
  return d1.getTime() - d2.getTime();
}

/**
 * Compare two IST time strings
 */
export function compareISTTimes(time1: string, time2: string): number {
  const [h1, m1, s1 = 0] = time1.split(":").map(Number);
  const [h2, m2, s2 = 0] = time2.split(":").map(Number);

  const totalSeconds1 = h1 * 3600 + m1 * 60 + s1;
  const totalSeconds2 = h2 * 3600 + m2 * 60 + s2;

  return totalSeconds1 - totalSeconds2;
}
